<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import type { UserSearchResultDTO } from '@/types'
import type {
  AdminPagedResponse,
  AuditAction,
  AuditActorType,
  AuditEntityType,
  AuditFieldChangeDTO,
  AuditLogDetailDTO,
  AuditLogFilterParams,
  AuditLogListItemDTO,
} from '@/types/admin'

import { adminService } from '@/services/adminService'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const { t, d } = useI18n()
const { setPageTitle } = useBreadcrumbs()
const route = useRoute()

const isLoading = ref(false)
const isDetailLoading = ref(false)
const error = ref<string | null>(null)
const detailError = ref<string | null>(null)

const logs = ref<AuditLogListItemDTO[]>([])
const totalCount = ref(0)
const page = ref(1)
const pageSize = ref(20)

const detailDialogVisible = ref(false)
const selectedDetail = ref<AuditLogDetailDTO | null>(null)
const logColumns = computed<TableColumn<AuditLogListItemDTO>[]>(() => [
  { accessorKey: 'occurredAt', header: t('pages.admin.audit.columns.occurredAt') },
  { accessorKey: 'entityType', header: t('pages.admin.audit.columns.entityType') },
  { accessorKey: 'entityId', header: t('pages.admin.audit.columns.entityId') },
  { accessorKey: 'action', header: t('pages.admin.audit.columns.action') },
  { id: 'actor', header: t('pages.admin.audit.columns.actor') },
  { accessorKey: 'source', header: t('pages.admin.audit.columns.source') },
  { accessorKey: 'operationName', header: t('pages.admin.audit.columns.operation') },
  { accessorKey: 'changeCount', header: t('pages.admin.audit.columns.changes') },
])
const changeColumns = computed<TableColumn<AuditFieldChangeDTO>[]>(() => [
  { accessorKey: 'field', header: t('pages.admin.audit.detail.field') },
  { accessorKey: 'oldValue', header: t('pages.admin.audit.detail.oldValue') },
  { accessorKey: 'newValue', header: t('pages.admin.audit.detail.newValue') },
])

const filters = reactive({
  from: null as Date | null,
  to: null as Date | null,
  entityType: '' as AuditEntityType | '',
  action: '' as AuditAction | '',
  actorType: '' as AuditActorType | '',
  actorUserId: '',
  entityId: '',
  eventId: null as number | null,
  registrationId: null as number | null,
  workspaceId: null as number | null,
})

const selectedUser = ref<UserSearchResultDTO | null>(null)

// A selected user either narrows to what they did (actor) or to changes made to their own
// account record (subject). The actor ID stored in the audit log is the user's ID.
type UserFilterMode = 'actor' | 'subject'
const userFilterMode = ref<UserFilterMode>('actor')
const userFilterModeOptions = computed(() => [
  { label: t('pages.admin.audit.filters.userFilterActor'), value: 'actor' as UserFilterMode },
  { label: t('pages.admin.audit.filters.userFilterSubject'), value: 'subject' as UserFilterMode },
])
const filtersByActor = computed(() => !!selectedUser.value && userFilterMode.value === 'actor')
const filtersBySubject = computed(() => !!selectedUser.value && userFilterMode.value === 'subject')

const entityOptions = [
  { label: 'Event', value: 'event' as AuditEntityType },
  { label: 'Registration', value: 'registration' as AuditEntityType },
  { label: 'Event Invite', value: 'eventInvite' as AuditEntityType },
  { label: 'Fee Set', value: 'feeSet' as AuditEntityType },
  { label: 'Group', value: 'group' as AuditEntityType },
  { label: 'Rule', value: 'rule' as AuditEntityType },
  { label: 'Workspace', value: 'workspace' as AuditEntityType },
  { label: 'User', value: 'user' as AuditEntityType },
]

const actionOptions = [
  { label: 'Create', value: 'create' as AuditAction },
  { label: 'Update', value: 'update' as AuditAction },
  { label: 'Delete', value: 'delete' as AuditAction },
  { label: 'Restore', value: 'restore' as AuditAction },
  { label: 'Login', value: 'login' as AuditAction },
  { label: 'Failed login', value: 'login_failed' as AuditAction },
  { label: 'Logout', value: 'logout' as AuditAction },
  { label: 'Impersonation', value: 'impersonate' as AuditAction },
  { label: 'Session active', value: 'session_active' as AuditAction },
]

const actorTypeOptions = [
  { label: 'User', value: 'user' as AuditActorType },
  { label: 'System', value: 'system' as AuditActorType },
  { label: 'Anonymous', value: 'anonymous' as AuditActorType },
]

function toStartOfLocalDayIso(value: Date | null) {
  if (!value) {
    return undefined
  }

  const next = new Date(value)
  next.setHours(0, 0, 0, 0)
  return next.toISOString()
}

function toEndOfLocalDayIso(value: Date | null) {
  if (!value) {
    return undefined
  }

  const next = new Date(value)
  next.setHours(23, 59, 59, 999)
  return next.toISOString()
}

const buildFilterParams = (): AuditLogFilterParams => {
  return {
    page: page.value,
    pageSize: pageSize.value,
    from: toStartOfLocalDayIso(filters.from),
    to: toEndOfLocalDayIso(filters.to),
    entityType: filtersBySubject.value ? 'user' : filters.entityType || undefined,
    entityId: filtersBySubject.value
      ? selectedUser.value!.id
      : filters.entityId.trim() || undefined,
    action: filters.action || undefined,
    actorType: filters.actorType || undefined,
    actorUserId: filtersByActor.value
      ? selectedUser.value!.id
      : filters.actorUserId.trim() || undefined,
    eventId: filters.eventId ?? undefined,
    registrationId: filters.registrationId ?? undefined,
    workspaceId: filters.workspaceId ?? undefined,
  }
}

const loadLogs = async () => {
  isLoading.value = true
  error.value = null

  try {
    const response = await adminService.getAuditLogs(buildFilterParams())
    const paged = response as AdminPagedResponse<AuditLogListItemDTO>
    logs.value = paged.items
    totalCount.value = paged.totalCount
  } catch (err) {
    console.error('Failed to load audit logs:', err)
    logs.value = []
    totalCount.value = 0
    error.value = t('pages.admin.audit.messages.loadError')
  } finally {
    isLoading.value = false
  }
}

const applyFilters = async () => {
  page.value = 1
  await loadLogs()
}

const resetFilters = async () => {
  filters.from = null
  filters.to = null
  filters.entityType = ''
  filters.action = ''
  filters.actorType = ''
  filters.actorUserId = ''
  filters.entityId = ''
  filters.eventId = null
  filters.registrationId = null
  filters.workspaceId = null
  selectedUser.value = null
  userFilterMode.value = 'actor'
  page.value = 1
  await loadLogs()
}

const onPage = async (nextPage: number) => {
  page.value = nextPage
  await loadLogs()
}

function selectLog(_event: Event, row: { original: AuditLogListItemDTO }) {
  void openDetail(row.original)
}

const openDetail = async (log: AuditLogListItemDTO) => {
  detailDialogVisible.value = true
  selectedDetail.value = null
  detailError.value = null
  isDetailLoading.value = true

  try {
    selectedDetail.value = await adminService.getAuditLogById(log.id)
  } catch (err) {
    console.error('Failed to load audit details:', err)
    detailError.value = t('pages.admin.audit.messages.detailLoadError')
  } finally {
    isDetailLoading.value = false
  }
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return 'null'
  }

  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

const formatActor = (log: AuditLogListItemDTO): string => {
  const actor = log.actorDisplayName || log.actorUserId || log.actorType
  if (!log.impersonatorDisplayName && !log.impersonatorUserId) {
    return actor
  }

  const impersonator = log.impersonatorDisplayName || log.impersonatorUserId
  return t('pages.admin.audit.actorVia', {
    actor,
    impersonator,
  })
}

// Deep link from the admin user page: /admin/audit?userId=<id>&userFilter=actor|subject
async function applyUserFromRoute() {
  const userId = typeof route.query.userId === 'string' ? route.query.userId : ''
  if (!userId) {
    return
  }

  userFilterMode.value = route.query.userFilter === 'subject' ? 'subject' : 'actor'
  try {
    const user = await adminService.getUserById(userId)
    selectedUser.value = {
      id: user.id,
      userName: user.userName ?? null,
      email: user.email ?? null,
      firstName: user.firstName ?? null,
      lastName: user.lastName ?? null,
      userType: null,
      source: 'local',
    }
  } catch (err) {
    // Still filter by the ID so the link works even if the user record is gone.
    console.error('Failed to load user for audit filter:', err)
    if (userFilterMode.value === 'actor') {
      filters.actorUserId = userId
    } else {
      filters.entityType = 'user'
      filters.entityId = userId
    }
  }
}

onMounted(async () => {
  setPageTitle(t('pages.admin.audit.title'))
  await applyUserFromRoute()
  await loadLogs()
})
</script>

<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold">{{ t('pages.admin.audit.title') }}</h1>
        <p class="text-muted">{{ t('pages.admin.audit.subtitle') }}</p>
      </div>
      <UButton
        icon="i-lucide-refresh-cw"
        :loading="isLoading"
        @click="loadLogs"
        variant="outline"
      />
    </div>

    <AppAlert v-if="error" color="error" :closable="false">
      {{ error }}
    </AppAlert>

    <UCard>
      <template #default>
        <div class="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{ t('pages.admin.audit.filters.from') }}</label>
            <AppDatePicker v-model="filters.from" show-icon icon-display="input" fluid />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{ t('pages.admin.audit.filters.to') }}</label>
            <AppDatePicker v-model="filters.to" show-icon icon-display="input" fluid />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{
              t('pages.admin.audit.filters.entityType')
            }}</label>
            <AppSelect
              v-model="filters.entityType"
              :options="entityOptions"
              option-label="label"
              option-value="value"
              show-clear
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{ t('pages.admin.audit.filters.action') }}</label>
            <AppSelect
              v-model="filters.action"
              :options="actionOptions"
              option-label="label"
              option-value="value"
              show-clear
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{ t('pages.admin.audit.filters.actorType') }}</label>
            <AppSelect
              v-model="filters.actorType"
              :options="actorTypeOptions"
              option-label="label"
              option-value="value"
              show-clear
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{
              t('pages.admin.audit.filters.actorUserId')
            }}</label>
            <UInput v-model="filters.actorUserId" :disabled="filtersByActor" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{
              t('pages.admin.audit.filters.userSearch')
            }}</label>
            <UserSearchSelect v-model="selectedUser" local-only fluid />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{
              t('pages.admin.audit.filters.userFilterMode')
            }}</label>
            <AppSelect
              v-model="userFilterMode"
              :options="userFilterModeOptions"
              option-label="label"
              option-value="value"
              :disabled="!selectedUser"
              fluid
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{ t('pages.admin.audit.filters.entityId') }}</label>
            <UInput v-model="filters.entityId" :disabled="filtersBySubject" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{ t('pages.admin.audit.filters.eventId') }}</label>
            <UInputNumber
              v-model="filters.eventId"
              :min="1"
              class="w-full"
              :increment="false"
              :decrement="false"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{
              t('pages.admin.audit.filters.registrationId')
            }}</label>
            <UInputNumber
              v-model="filters.registrationId"
              :min="1"
              class="w-full"
              :increment="false"
              :decrement="false"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm text-muted">{{
              t('pages.admin.audit.filters.workspaceId')
            }}</label>
            <UInputNumber
              v-model="filters.workspaceId"
              :min="1"
              class="w-full"
              :increment="false"
              :decrement="false"
            />
          </div>
          <div class="flex items-end gap-2">
            <UButton :label="t('common.actions.filter')" @click="applyFilters" />
            <UButton
              :label="t('common.actions.reset')"
              color="neutral"
              @click="resetFilters"
              variant="outline"
            />
          </div>
        </div>
      </template>
    </UCard>

    <AppDataTableShell :data="logs" :columns="logColumns" :loading="isLoading" @select="selectLog">
      <template #occurredAt-cell="{ row }">
        {{ d(new Date(row.original.occurredAt), 'datetimefull') }}
      </template>
      <template #actor-cell="{ row }">{{ formatActor(row.original) }}</template>
    </AppDataTableShell>
    <div v-if="totalCount > pageSize" class="mt-3 flex justify-end">
      <UPagination
        :page="page"
        :total="totalCount"
        :items-per-page="pageSize"
        @update:page="onPage"
      />
    </div>

    <AppModal
      v-model:open="detailDialogVisible"
      modal
      :title="t('pages.admin.audit.detail.title')"
      :style="{ width: 'min(1000px, 95vw)' }"
    >
      <div class="space-y-4">
        <AppAlert v-if="detailError" color="error" :closable="false">
          {{ detailError }}
        </AppAlert>

        <div v-if="isDetailLoading" class="space-y-2">
          <USkeleton height="2rem" />
          <USkeleton height="2rem" />
          <USkeleton height="2rem" />
        </div>

        <template v-else-if="selectedDetail">
          <div class="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            <div><strong>ID:</strong> {{ selectedDetail.id }}</div>
            <div>
              <strong>{{ t('pages.admin.audit.columns.action') }}:</strong>
              {{ selectedDetail.action }}
            </div>
            <div>
              <strong>{{ t('pages.admin.audit.columns.entityType') }}:</strong>
              {{ selectedDetail.entityType }}
            </div>
            <div>
              <strong>{{ t('pages.admin.audit.columns.entityId') }}:</strong>
              {{ selectedDetail.entityId }}
            </div>
            <div>
              <strong>{{ t('pages.admin.audit.columns.occurredAt') }}:</strong>
              {{ d(new Date(selectedDetail.occurredAt), 'datetimefull') }}
            </div>
            <div>
              <strong>{{ t('pages.admin.audit.columns.actor') }}:</strong>
              {{ formatActor(selectedDetail) }}
            </div>
            <div v-if="selectedDetail.impersonatorDisplayName || selectedDetail.impersonatorUserId">
              <strong>{{ t('pages.admin.audit.detail.impersonator') }}:</strong>
              {{ selectedDetail.impersonatorDisplayName || selectedDetail.impersonatorUserId }}
            </div>
          </div>

          <AppDataTableShell :data="selectedDetail.changes" :columns="changeColumns">
            <template #oldValue-cell="{ row }">
              <pre class="text-xs whitespace-pre-wrap">{{
                formatValue(row.original.oldValue)
              }}</pre>
            </template>
            <template #newValue-cell="{ row }">
              <pre class="text-xs whitespace-pre-wrap">{{
                formatValue(row.original.newValue)
              }}</pre>
            </template>
          </AppDataTableShell>
        </template>
      </div>
    </AppModal>
  </div>
</template>
